import moment from 'moment';
import ShelterlyPDF from '../utils/pdf';
import { capitalize } from '../utils/formatString';
import { SpeciesIcon } from '../components/icons';
import { DATE_FORMAT } from '../constants';
import { createQrCode } from '../utils/qrCode';
import { buildVetRelease, buildLiabiltyRelease, buildAnimalRelease } from '../people/Utils';

/**
 * helper function for drawing the animal details QR code at the top of the animal care schedule pdf
 */
async function drawQRCareCode(pdf, animalUrl) {
  // set absolute position to the top header of each animal care schedule page
  const { oldLeftMargin, oldLastYPosition, resetPosition } =
  pdf.setAbsolutePosition({ x: 200, y: 10 });

  // draw QR code
  await pdf.drawImage({ src: createQrCode(animalUrl) });

  // reset position to continue normal draw operations
  resetPosition(oldLeftMargin, oldLastYPosition);
}

async function buildAnimalCareScheduleContent(pdf, animals) {
  for (let i = 0; i < animals.length; i++) {
    const animal = animals[i];

    if (i > 0) {
      pdf.drawPageBreak();
    }

    if (animal.url) {
      await drawQRCareCode(pdf, animal.url)
    }

    // checking for risk indications
    let riskIndication = false;
    [animal.aggressive, animal.aco_required, animal.injured].forEach((riskValue) => {
      if (riskValue.toString().toLowerCase() === 'yes') {
        riskIndication = true;
      }
    })
    if (riskIndication) {
      pdf.resetDocumentLeftMargin();
      pdf.drawPad(-12);
      pdf.drawHRect({ color: 'RISKY_RED' });
      pdf.setDocumentColors({ rgb: [255, 255, 255]});
      pdf.drawPad(9)
      pdf.documentLeftMargin = pdf.pageWidth / 2 - 5;
      pdf.drawSingleLineText({ text: '***!!!***' });
      pdf.setDocumentColors();
      pdf.resetDocumentLeftMargin();
    }

    const imageSrc = animal.front_image;
    let graphicOptions = {
      display: 'inline',
      maxHeight: 75,
      maxWidth: 75,
      padding: [10, 0, 10, 50],
      drawFuncName: 'drawImage'
    };
    if (animal.lazyImage) {
      graphicOptions = {
        ...graphicOptions,
        src: animal.lazyImage,
        drawFuncName: 'drawGraphic'
      };
    }
    else if (imageSrc) {
      graphicOptions = {
        ...graphicOptions,
        src: imageSrc
      };
    } else  {
      graphicOptions = {
        ...graphicOptions,
        drawFuncName: 'drawSvg',
        svg: <SpeciesIcon color="#B9BECA" species={animal.species_string} />
      };
    }
    // draw image or svg
    await pdf[graphicOptions.drawFuncName](graphicOptions)
      .catch(() => {
        if (imageSrc) {
          console.log('Cannot add animal image to ShelterlyPDF');
        }
      });

    pdf.setDocumentFontSize({ size: 12 });
    pdf.drawPad(-15);

    const labelsList = [
      [`Animal No: ***A#${animal.id_for_incident}***`, animal.animal_count > 1 ? `Animal Count: ${animal.animal_count}` : `Animal Name: ***${capitalize(animal.name || 'Unknown Name')}***`],
      [
        `Intake Date: ${animal.intake_date
          ? new Date(animal.intake_date).toLocaleDateString()
          : 'N/A'}`,
          `Species: ${capitalize(animal.species_string)}`
      ],
      [
        `Color: ${capitalize(animal.pcolor)} ${animal.scolor ? `/ ${capitalize(animal.scolor)}` : '' }`,
        `Age: ${capitalize(animal.age)}`
      ],
      [
        `Under Vet Care: Y / N`,
        animal.shelter_object
          ? ''
          : 'Location: N/A'
      ]
    ];
    const listOptions = {
      listStyle: 'inline',
      labelInlineOffset: 125
    };
    labelsList.forEach((labels) => {
      pdf.drawTextList({ labels, ...listOptions })
    });

    pdf.resetDocumentLeftMargin();
    pdf.drawPad(15);

    pdf.drawWrappedText({
      text: `Location: ${
        animal.shelter_object ? `${animal.shelter_object.name}` : "N/A"
      }${
        animal.building_name ? ` / ${animal.building_name}` : ""
      }${
        animal.room ? ` / ${animal.room_name}` : ""
      }`,
    });

    // draw status
    pdf.drawWrappedText({ text: `Status: ${animal.status.toUpperCase() }`});

    if (animal.owners && animal.owners.length) {
      pdf.drawWrappedText({ text: `Owner(s): ${animal.owners.map((owner) =>
        `${capitalize(`${owner.first_name} ${owner.last_name}`, { proper: true })}`).join('; ')}`})
    }
    else if (animal.owner_names && animal.owner_names.length) {
        pdf.drawWrappedText({ text: `Owner(s): ${animal.owner_names.map((owner_name) =>
          `${capitalize(owner_name, { proper: true })}`).join('; ')}`})
    }
    else {
      pdf.drawWrappedText({ text: 'Owner(s): ___________________________________'})
    }

    pdf.drawPad(-15);

    const additionalLabelsList = [
      [`Aggressive: ${capitalize(animal.aggressive)}`, `Injured: ${capitalize(animal.injured)}`, `Altered: ${capitalize(animal.fixed)}`, 'Sex: ' + `${animal.sex || '_______'}`],
      [`Microchip: ${animal.microchip || '_______'}`, 'Neck Tag: _______', 'Collar: _______', ' ']
    ]
    const additionalListOptions = {
      listStyle: 'inline',
      labelInlineMarginRight: 75
    };
    additionalLabelsList.forEach((labels) => {
      pdf.drawTextList({ labels, ...additionalListOptions})
    });

    pdf.drawPad(25)

    pdf.drawWrappedText({
      text: `Breed / Description: ${animal.color_notes || 'N/A'}`,
      linePadding: -2,
      bottomPadding: 3
    });
    pdf.drawWrappedText({
      text: `Animal Notes: ${animal.behavior_notes || 'N/A'}`,
      linePadding: -2,
      bottomPadding: 3,
    });
    pdf.drawWrappedText({
      text: `Medical Notes: ${animal.medical_notes || 'N/A'}`,
      linePadding: -2
    });

    pdf.drawHRule({
      buffer: 5
    });

    const pageWidth = pdf.pageWidth - pdf.documentLeftMargin * 2;
    const smallCol = pageWidth * .15;
    const bigCol = pageWidth * .35;
    pdf.drawTableGrid({
      headers: ['Date Time', 'Intials/ID', 'Actions', 'Comments'],
      columnStyles: [{ cellWidth: smallCol }, { cellWidth: smallCol }, { cellWidth: bigCol }, { cellWidth: bigCol }]
    });
  }

  return pdf;
}

async function buildAnimalMedicalHeader(pdf, full_medical){
  const animal = full_medical['animal_object'];
  const listOptions = {
    listStyle: 'inline',
  };
  const labelsList = [
    [
      `Animal No: ***A#${animal.id_for_incident}***`,
      animal.animal_count > 1 ? `Animal Count: ${animal.animal_count}` : `Animal Name: ***${capitalize(animal.name || 'Unknown Name')}***`,
      `Intake Date: ${animal.intake_date ? new Date(animal.intake_date).toLocaleDateString() : 'N/A'}`
    ],
    [
      `Species: ${capitalize(animal.species_string)}`,
      `Color: ${capitalize(animal.pcolor)} ${animal.scolor ? `/ ${capitalize(animal.scolor)}` : '' }`,
      `Age: ${capitalize(animal.age)}`
    ],
    [
      `Aggressive: ${capitalize(animal.aggressive)}`,
      `Altered: ${capitalize(animal.fixed)}`,
      `Sex: ${capitalize(animal.sex)}`,
    ]
  ];

  labelsList.forEach((labels) => {
    pdf.drawTextList({ labels, ...listOptions })
  });

  pdf.resetDocumentLeftMargin();
  pdf.drawPad(15);

  pdf.drawWrappedText({
    text: `Location: ${
      animal.shelter_object ? `${animal.shelter_object.name}` : "N/A"
    }${
      animal.building_name ? ` / ${animal.building_name}` : ""
    }${
      animal.room ? ` / ${animal.room_name}` : ""
    }`,
  });

  pdf.drawHRule({
    buffer: 5
  });

  pdf.drawWrappedText({ text: `Owner(s):`})
  if (animal.owners && animal.owners.length) {
    animal.owners.forEach((owner) => {
      pdf.drawWrappedText({ text: `Name: ${owner.first_name} ${owner.last_name}`})
      pdf.drawWrappedText({ text: `Address: ${owner.full_address}`})
      pdf.drawWrappedText({ text: `Phone: ${owner.phone}`})
      pdf.drawWrappedText({ text: `Email: ${owner.email}`})
      pdf.drawPad(5)
    })
  }
  else{
    pdf.drawWrappedText({ text: `N/A`})
  }

  pdf.drawHRule({
    buffer: 5
  });

  pdf.drawPad(5);

  pdf.drawWrappedText({
    text: `Microchip: ${animal.microchip || 'N/A'}`,
  });

  pdf.drawWrappedText({
    text: `Breed / Description: ${animal.color_notes || 'N/A'}`,
  });
  pdf.drawWrappedText({
    text: `Medical Notes: ${animal.medical_notes || 'N/A'}`,
  });

  pdf.drawHRule({
    buffer: 5
  });

  pdf.drawWrappedText({
    text: `Medical Plan: ${full_medical.medical_plan || 'N/A'}`,
  });
  pdf.drawWrappedText({
    text: `Diagnosis: ${full_medical.diagnosis_text || 'N/A'}`,
  });
  pdf.drawWrappedText({
    text: `Diagnosis Note: ${full_medical.diagnosis_notes || 'N/A'}`,
  });

  pdf.drawHRule({
    buffer: 5
  });
  return pdf;
}

async function buildAnimalEMRContent(pdf, animals) {
  const listOptions = {
    listStyle: 'inline',
  };
  for (let i = 0; i < animals.length; i++) {
    const full_medical = animals[i];
    const animal = full_medical['animal_object'];

    if (i > 0) {
      pdf.drawPageBreak();
    }

    pdf.setDocumentFontSize({ size: 12 });
    pdf.drawPad(-15);

    pdf = await buildAnimalMedicalHeader(pdf, full_medical);

    full_medical.vet_requests.forEach((vr) => {
      pdf.drawPageBreak()
      const exam = full_medical?.exams?.find(
        e => e.vet_request === vr.id
      );
      pdf.drawWrappedText({
        text: `***VR#: ${vr.id_for_incident || 'N/A'}***`,
      });
      pdf.drawWrappedText({
        text: `Presenting Complaints: ${vr.complaints_text || 'N/A'}`,
      });
      if (exam){
        pdf.drawHRule({
          buffer: 5
        });
        const examLabelList = [
          [
            `***Exam#: ${exam.id || 'N/A'}***`,
            `Performed: ${new Date(exam.open).toLocaleDateString()}`,
            `Examiner: ${exam.assignee_object.first_name} ${exam.assignee_object.last_name}`,
          ],
          [
            `Weight: ${exam.weight || 'N/A'}`,
            `Temperature (F): ${exam.temperature || 'N/A'}`,
            `Temperature Method: ${exam.temperature_method || 'N/A'}`,
          ],
          [
            `Pulse: ${exam.pulse || 'N/A'}`,
            `Respiratory Rate: ${exam.respiratory_rate || 'N/A'}`,
            ``
          ]
        ];
        examLabelList.forEach((labels) => {
          pdf.drawTextList({ labels, ...listOptions })
        });
        pdf.drawPad(10);
        const answerStrings = exam.answers.map((ea) =>
          `${ea.name}: ${ea.answer || 'N/A'}${ea.answer_notes ? `. ${ea.answer_notes}` : ''}`
        );

        const answerLabelList = [];

        for (let i = 0; i < answerStrings.length; i += 2) {
          answerLabelList.push([
            answerStrings[i] || '',
            answerStrings[i + 1] || ''
          ]);
        }
        answerLabelList.forEach((labels) => {
          pdf.drawTextList({ labels, ...listOptions })
        });
      }
    })
    if (full_medical.diagnostic_objects.length > 0 || full_medical.procedure_objects.length > 0 || full_medical.treatment_plans.length > 0){
      pdf.drawPageBreak()
      if (full_medical.diagnostic_objects.length > 0){
        pdf.drawWrappedText({
          text: `***Diagnostics***`
        });
        full_medical.diagnostic_objects.forEach((diag) => {
          pdf.drawWrappedText({
            text:
              `Diagnostic: ${diag.name || 'N/A'}, ` +
              `Completed: ${
                diag.complete
                  ? new Date(diag.complete).toLocaleDateString()
                  : 'Pending'
              }, ` +
              `Result: ${diag.result || 'N/A'}`
          });
        });
        pdf.drawHRule({
          buffer: 5
        });
      }

      if (full_medical.procedure_objects.length > 0){
        pdf.drawPad(10);
        pdf.drawWrappedText({
          text: `***Procedures***`
        });
        full_medical.procedure_objects.forEach((proc) => {
          pdf.drawWrappedText({
            text:
              `Diagnostic: ${proc.name || 'N/A'}, ` +
              `Completed: ${
                proc.complete
                  ? new Date(proc.complete).toLocaleDateString()
                  : 'Pending'
              }, ` +
              `Result: ${proc.result || 'N/A'}`
          });
        });
        pdf.drawHRule({
          buffer: 5
        });
      }

      if (full_medical.treatment_plans.length > 0){
        pdf.drawPad(10);
        pdf.drawWrappedText({
          text: `***Treatments***`
        });
        full_medical.treatment_plans.forEach((tp) => {
          pdf.drawWrappedText({text: `Treatment: ${tp.description || 'N/A'}`})
          if (tp.treatment_requests.length > 0){
            tp.treatment_requests.forEach((tr) => {
              const admin_time = tr.actual_admin_time || tr.suggested_admin_time;
              const formatted_admin_time = admin_time ?
                new Date(admin_time).toLocaleString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })
              : "N/A";
              pdf.drawWrappedText({
                text:
                  `- Admin Time: ${formatted_admin_time}, ` +
                  `Quantity: ${tr.quantity}, ` +
                  `Unit: ${tr.unit}, ` +
                  `Route: ${tr.route}`
              });
            })

          }
        });
      }
    }

  }

  return pdf;
}


async function buildAnimalDischargeContent(pdf, animals) {
  for (let i = 0; i < animals.length; i++) {
    const full_medical = animals[i];
    const animal = full_medical['animal_object'];

    if (i > 0) {
      pdf.drawPageBreak();
    }

    pdf.setDocumentFontSize({ size: 12 });
    pdf.drawPad(-15);

    pdf = await buildAnimalMedicalHeader(pdf, full_medical);

    pdf.drawWrappedText({
      text: `Discharge Notes: ${full_medical.discharge_summary || 'N/A'}`,
    });

  }

  return pdf;
}

function buildAnimalCountList(pdf, animals, {
  countLabelMarginTo = 0
} = {}) {
  const animalCounts = [];
  animals
    .forEach((animal) => {
      const countIndex = animalCounts.findIndex(([species]) => ((animal.species_string ? animal.species_string : (animal.species || 'other')) === species));
      if (countIndex > -1) {
        const [currentSpecies, oldCount] = animalCounts[countIndex];
        animalCounts[countIndex] = [currentSpecies, Number(oldCount) + (animal.animal_count ? Number(animal.animal_count) : 1)];
      } else {
        animalCounts.push([animal.species_string ? animal.species_string : (animal.species || 'other'), (animal.animal_count ? animal.animal_count : 1)]);
      }
    });
    // Sort alphabetically by species name
    animalCounts.sort(([a], [b]) => a.localeCompare(b));
    pdf.drawTextList({
      labels: animalCounts.map(([species, count]) => (
        // capitalize the species
        `${species.replace(/(^.)/, m => m.toUpperCase())}: ${count}`
      )),
      labelMarginTop: countLabelMarginTo
    });
}

/**
 * generates care schedule for one animal
 * @param  {Array} animals - an array of the animal objects
 * @param  {ShelterlyPDF} pdf - pre-instantiated ShelterlyPDF instance, creates a new one by default
 * @returns {ShelterlyPDF}
 */
async function buildAnimalCareScheduleDoc (animals) {
  const pdf = new ShelterlyPDF({}, {
    pageTitle: 'Animal Care Schedule',
    pageSubtitle: `Date: ${new Date().toLocaleDateString()}`,
    drawHeaderOnEveryPage: true
  });

  return buildAnimalCareScheduleContent(pdf, animals);
}

async function printAnimalCareSchedule (animal = {}) {
  const pdf = await buildAnimalCareScheduleDoc([animal]);
  pdf.fileName = pdf.filename || `Shelterly-Animal-Care-Schedule-${animal.id_for_incident.toString().padStart(3, 0)}-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
};

async function printAllAnimalCareSchedules (animals = []) {
  // sort animals by id
  const sortedAnimals = [...animals].sort((a,b) => a.id_for_incident - b.id_for_incident);

  const  pdf = await buildAnimalCareScheduleDoc(sortedAnimals);
  pdf.fileName = `Shelterly-Animal-Care-Schedules-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
}

async function buildAnimalSummaryContent(pdf, animals, organization, release_options=['vet', 'liability', 'release']){
  for (let i = 0; i < animals.length; i++) {
    const animal = animals[i];

    if (i > 0) {
      pdf.drawPageBreak();
    }

    const animalInfoList = [
      `ID: A#${animal.id_for_incident}`,
      `Status: ${capitalize(animal.status.toLowerCase(), { proper: true })}`,
      `Name: ${animal.name || 'Unknown Name'}`,
      `Species: ${capitalize(animal.species_string)}, Count: ${animal.animal_count}`,
      `Sex: ${capitalize(animal.sex|| 'Unknown')}`,
      `Age: ${capitalize(animal.age || 'Unknown')}`,
      `Size: ${capitalize(animal.size || 'Unknown')}`,
      `Primary Color: ${capitalize(animal.pcolor || 'N/A')}, Secondary Color: ${capitalize(animal.scolor || 'N/A')}`,
      `Special Handling Required: ${animal.aco_required ? capitalize(animal.aco_required) : 'N/A'}`,
      `Altered: ${animal.fixed ? capitalize(animal.fixed) : 'N/A'}`,
      `Confined: ${animal.confined ? capitalize(animal.confined) : 'N/A'}`,
      `Aggressive: ${animal.aggressive ? capitalize(animal.aggressive) : 'N/A'}`,
      `Injured: ${animal.injured ? capitalize(animal.injured) : 'N/A'}`,
      `Microchip: ${animal.microchip ? capitalize(animal.microchip) : 'N/A'}`,
      `External ID: ${animal.external_id ? capitalize(animal.external_id) : 'N/A'}`
    ];

    pdf.drawTextList({
      labels: animalInfoList,
      listStyle: 'grid',
    });
    pdf.drawPad(15);
    pdf.drawWrappedText({
      text: `Breed/Description: ${animal.color_notes || 'N/A'}`,
    });
    pdf.drawWrappedText({
      text: `Animal Notes: ${animal.behavior_notes ? animal.behavior_notes : animal.animal_notes || 'N/A'}`,
    });
    pdf.drawWrappedText({
      text: `Medical Notes: ${animal.medical_notes || 'N/A'}`,
    });
    pdf.drawHRule();
    pdf.drawWrappedText({text: 'Owner/Responsible Party'})
    animal.owners.forEach(owner => {
      const ownerInfoList = [
        `Name: ${owner.first_name} ${owner.last_name}`,
        `Owner ID: #${owner.id || 'N/A'}`,
        `Agency: ${owner.agency || 'N/A'}`,
        `Telephone: ${owner.display_phone || 'N/A'} ${owner.display_alt_phone ? `  Alt: ${owner.display_alt_phone}` : ''}`,
        `Email: ${owner.email || 'N/A'}`
      ];

      if (owner.address) {
          ownerInfoList.push(`Address: ${owner.full_address}`);
      } else {
        ownerInfoList.push('Address: N/A');
      }
      pdf.drawTextList({
        labels: ownerInfoList,
      });
      pdf.drawPad(10);
      pdf.drawWrappedText({
        text: `Comments: ${owner.comments || 'N/A'}`
      });
    });
    release_options.forEach(release_option => {
      if (release_option == 'vet'){
        pdf.drawPageBreak();
        pdf = buildVetRelease(pdf, organization);
      }
      else if (release_option == 'liability'){
        pdf.drawPageBreak();
        pdf = buildLiabiltyRelease(pdf, organization);
      }
      else if (release_option == 'release'){
        pdf.drawPageBreak();
        pdf = buildAnimalRelease(pdf, organization);
      }
    })
  }
  return pdf;
}

async function buildAnimalSummaryDoc (data = {}) {
  // sort animals by id
  console.log(data);
  const sortedAnimals = [...data.animals].sort((a,b) => a.id_for_incident - b.id_for_incident);
  const pdf = new ShelterlyPDF({}, {
    pageTitle: 'Animal Summary',
    pageSubtitle: `Organization: ${data.organization} | Incident: ${data.incident} | Date: ${new Date().toLocaleDateString()}`,
    drawHeaderOnEveryPage: true
  });

  return buildAnimalSummaryContent(pdf, sortedAnimals, data.organization);
}


async function buildAnimalEMRDoc (animals) {
  const pdf = new ShelterlyPDF({}, {
    pageTitle: 'Animal EMR',
    pageSubtitle: `Date: ${new Date().toLocaleDateString()}`,
    drawHeaderOnEveryPage: true
    });

    return buildAnimalEMRContent(pdf, animals);
}

async function printAnimalEMR (full_animal = {}) {
  const pdf = await buildAnimalEMRDoc([full_animal]);
  const animal = full_animal['animal_object'];
  pdf.fileName = pdf.filename || `Shelterly-Animal-EMR-${animal.id_for_incident.toString().padStart(3, 0)}-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
};

async function buildAnimalDischargeDoc (animals) {
  const pdf = new ShelterlyPDF({}, {
    pageTitle: 'Animal EMR',
    pageSubtitle: `Date: ${new Date().toLocaleDateString()}`,
    drawHeaderOnEveryPage: true
    });

    return buildAnimalDischargeContent(pdf, animals);
}

async function printAnimalDischarge (full_animal = {}) {
  const pdf = await buildAnimalDischargeDoc([full_animal]);
  const animal = full_animal['animal_object'];
  pdf.fileName = pdf.filename || `Shelterly-Animal-Discharge-${animal.id_for_incident.toString().padStart(3, 0)}-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
}

async function printAnimalSummary (data = {}) {
  const pdf = await buildAnimalSummaryDoc(data);
  pdf.fileName = pdf.filename || `Shelterly-Animal-Summary-${data.animals[0].id_for_incident.toString().padStart(3, 0)}-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
};

async function printAllAnimalSummaries (data) {
  const  pdf = await buildAnimalSummaryDoc(data);
  pdf.fileName = `Shelterly-Animal-Summaries-${moment().format(DATE_FORMAT)}`;
  return pdf.saveFile();
}

export {
  buildAnimalCareScheduleContent,
  buildAnimalCareScheduleDoc,
  buildAnimalEMRContent,
  buildAnimalEMRDoc,
  buildAnimalSummaryContent,
  buildAnimalSummaryDoc,
  buildAnimalCountList,
  printAnimalCareSchedule,
  printAllAnimalCareSchedules,
  printAnimalEMR,
  printAnimalDischarge,
  printAnimalSummary,
  printAllAnimalSummaries
};
